"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useState, useMemo, useEffect, useRef } from "react";

import { ListingStatus } from "@/.zenstack/logical-prisma-client";
import { useSession } from "@/lib/auth/client";
import {
  useFindManySavedListing,
  useUpsertSavedListing,
} from "@/lib/hooks/generated/tanstack-query";

export interface SavedListingInfo {
  id: string;
  listingId: string;
  personId: string;
  isSaved: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export class SavedListingDao {
  // Constants for pagination and filtering
  static readonly MAX_FAVORITES_PER_PAGE = 25;
  static readonly ARCHIVED_LISTING_DAYS_THRESHOLD = 30;
  private static readonly CACHE_KEY_PREFIX = "saved-listings";

  /**
   * Get the cache key for a user's saved listings
   */
  static getCacheKey(userId: string): string {
    if (!userId) {
      throw new Error("Cannot create cache key for empty userId");
    }
    return `${this.CACHE_KEY_PREFIX}:${userId}`;
  }

  /**
   * Check if a cache key is valid (has a non-empty userId)
   */
  static isValidCacheKey(cacheKey: string): boolean {
    return cacheKey.includes(":") && !cacheKey.endsWith(":");
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function queryKeyHasFavoritesFilter(key: unknown): boolean {
  if (!isRecord(key)) return false;
  const obj = key as Record<string, unknown>;

  const whereCandidate = obj["where"];
  const inputCandidate = obj["input"];
  const inputWhereCandidate = isRecord(inputCandidate)
    ? (inputCandidate as Record<string, unknown>)["where"]
    : undefined;

  const where = isRecord(whereCandidate)
    ? (whereCandidate as Record<string, unknown>)
    : isRecord(inputWhereCandidate)
      ? (inputWhereCandidate as Record<string, unknown>)
      : undefined;

  const candidates: unknown[] = [
    obj,
    where,
    isRecord(where)
      ? (where as Record<string, unknown>)["savedListings"]
      : undefined,
    obj["savedListings"],
  ];

  for (const c of candidates) {
    if (!isRecord(c)) continue;
    const savedListingsCandidate = (c as Record<string, unknown>)[
      "savedListings"
    ];
    const savedListings = isRecord(savedListingsCandidate)
      ? (savedListingsCandidate as Record<string, unknown>)
      : (c as Record<string, unknown>);

    const someCandidate =
      (savedListings as Record<string, unknown>)["some"] ??
      (savedListings as Record<string, unknown>)["every"];

    if (isRecord(someCandidate)) {
      if ((someCandidate as Record<string, unknown>)["isSaved"] === true)
        return true;
      if (Object.prototype.hasOwnProperty.call(someCandidate, "personId"))
        return true;
    }

    if (Object.prototype.hasOwnProperty.call(c, "savedByUserId")) return true;
  }

  for (const v of Object.values(obj)) {
    if (isRecord(v) && queryKeyHasFavoritesFilter(v)) return true;
  }
  return false;
}

/**
 * React hook for managing saved listings with caching
 */
export function useSavedListings() {
  const { data: session, isPending: isAuthLoading } = useSession();
  const user = session?.user;
  const queryClient = useQueryClient();

  // Track which listing is currently being updated
  const [pendingListingId, setPendingListingId] = useState<string | null>(null);

  // Maintain a daily-updating reference point (start of today) to avoid stale 30-day thresholds
  const [startOfToday, setStartOfToday] = useState<Date>(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });

  // Use ref to track timeout and prevent infinite loops
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const now = new Date();
    const nextMidnight = new Date(now);
    nextMidnight.setDate(now.getDate() + 1);
    nextMidnight.setHours(0, 0, 0, 0);
    const msUntilNextMidnight = nextMidnight.getTime() - now.getTime();

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      setStartOfToday(d);
    }, msUntilNextMidnight + 1000); // small buffer

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []); // Remove startOfToday from dependencies to prevent infinite loop

  const thirtyDaysAgo = useMemo(() => {
    const d = new Date(startOfToday);
    d.setDate(d.getDate() - SavedListingDao.ARCHIVED_LISTING_DAYS_THRESHOLD);
    return d;
  }, [startOfToday]);

  // Only create cache key for authenticated users
  const cacheKey = useMemo(() => {
    if (!user?.id || isAuthLoading) {
      return null;
    }
    return SavedListingDao.getCacheKey(user.id);
  }, [user?.id, isAuthLoading]);

  // Query for fetching saved listings using TanStack Query
  const {
    data: rawSavedListings = [],
    isLoading,
    error,
    refetch,
  } = useFindManySavedListing(
    {
      where: {
        personId: user?.id || "",
        isSaved: true,
        // Avoid dynamic Date in query params; filter status-only server-side
        listing: {
          NOT: {
            OR: [
              { status: ListingStatus.ARCHIVED },
              { status: ListingStatus.HIDDEN },
            ],
          },
        },
      },
      take: SavedListingDao.MAX_FAVORITES_PER_PAGE,
    },
    {
      enabled: !!cacheKey && !!user && !isAuthLoading,
      staleTime: 15 * 60 * 1000, // 5 minutes
    },
  );

  // Client-side filter to handle different listing statuses
  const savedListings = useMemo(() => {
    return rawSavedListings.filter(
      (
        sl: SavedListingInfo & {
          listing?: { status?: string; updatedAt?: Date | string };
        },
      ) => {
        if (!sl?.isSaved) return false;
        const status = sl?.listing?.status as string | undefined;
        if (!status) return true;

        // Keep all saved listings regardless of status for favorites page filtering
        // The favorites page will handle status-based filtering separately
        if (status === ListingStatus.CLOSED) {
          const updatedAt = sl?.listing?.updatedAt
            ? new Date(sl.listing.updatedAt)
            : undefined;
          if (updatedAt && updatedAt < thirtyDaysAgo) return false;
        }
        return true;
      },
    );
  }, [rawSavedListings, thirtyDaysAgo]);

  // Create a Set for O(1) lookup performance
  const savedListingIds = useMemo(
    () =>
      new Set(
        savedListings
          .filter((sl: SavedListingInfo) => sl.isSaved)
          .map((sl: SavedListingInfo) => sl.listingId),
      ),
    [savedListings],
  );

  // // Debug logging to see the actual cache keys
  // useEffect(() => {
  //   if (process.env.NODE_ENV === "development" && status === "authenticated") {
  //     const allQueries = queryClient.getQueryCache().getAll();
  //     const savedListingQueries = allQueries.filter((query) =>
  //       query.queryKey.some(
  //         (key) => typeof key === "string" && key.includes("SavedListing"),
  //       ),
  //     );
  //     console.log("SavedListingDao cache keys:", {
  //       savedListingQueries: savedListingQueries.map((q) => q.queryKey),
  //       savedListings,
  //       savedListingIds: Array.from(savedListingIds),
  //     });
  //   }
  // }, [savedListings, savedListingIds, status, queryClient]);

  // Mutations using TanStack Query
  const upsertSavedListing = useUpsertSavedListing({
    onMutate: async (variables) => {
      if (!cacheKey) return { previousSavedListings: undefined };

      // Debug logging
      if (process.env.NODE_ENV === "development") {
        console.log("SavedListingDao onMutate:", {
          variables,
          cacheKey,
          userId: user?.id,
        });
      }

      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: ["zenstack", "SavedListing"],
      });

      // Snapshot the previous value
      const previousSavedListings = queryClient.getQueryData([
        "SavedListing",
        "findMany",
      ]);

      // Debug logging
      if (process.env.NODE_ENV === "development") {
        console.log(
          "SavedListingDao previousSavedListings:",
          previousSavedListings,
        );
      }

      // Extract the listingId from the mutation variables
      let listingId: string | null = null;

      if (variables.where?.listingId_personId?.listingId) {
        listingId = variables.where.listingId_personId.listingId;
      } else if (variables.where?.id && previousSavedListings) {
        const existing = (previousSavedListings as SavedListingInfo[]).find(
          (sl) => sl.id === variables.where?.id,
        );
        listingId = existing?.listingId || null;
      }

      if (listingId) {
        // Set the pending listing ID
        setPendingListingId(listingId);

        const newSavedStatus =
          typeof variables.create?.isSaved === "boolean"
            ? variables.create.isSaved
            : typeof variables.update?.isSaved === "boolean"
              ? variables.update.isSaved
              : true;

        const optimisticPersonId =
          (typeof variables.create?.personId === "string" &&
            variables.create.personId) ||
          (user?.id ?? "");

        // Debug logging
        if (process.env.NODE_ENV === "development") {
          console.log("SavedListingDao optimistic update:", {
            listingId,
            newSavedStatus,
            cacheKey: ["zenstack", "SavedListing"],
          });
        }

        // Update the cache optimistically
        queryClient.setQueryData(
          ["zenstack", "SavedListing"],
          (old: SavedListingInfo[] | undefined) => {
            if (!old) return old;

            const existingIndex = old.findIndex(
              (sl: SavedListingInfo) => sl.listingId === listingId,
            );
            if (existingIndex >= 0) {
              // Update existing record
              const updated = [...old];
              const existing = updated[existingIndex];
              if (existing) {
                updated[existingIndex] = {
                  ...existing,
                  isSaved: newSavedStatus,
                  updatedAt: new Date(),
                } as SavedListingInfo;
              }
              return updated;
            } else {
              // Add new record
              return [
                ...old,
                {
                  id: `temp-${Date.now()}`,
                  listingId,
                  personId: optimisticPersonId,
                  isSaved: newSavedStatus,
                  createdAt: new Date(),
                  updatedAt: new Date(),
                } as SavedListingInfo,
              ];
            }
          },
        );
      }

      // Return a context object with the snapshotted value
      return { previousSavedListings };
    },
    onError: (_err, _variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      const typedContext = context as
        | { previousSavedListings?: SavedListingInfo[] }
        | undefined;
      if (typedContext?.previousSavedListings) {
        queryClient.setQueryData(
          ["zenstack", "SavedListing"],
          typedContext.previousSavedListings,
        );
      }
      // Clear the pending listing ID
      setPendingListingId(null);
    },
    onSuccess: (data, variables) => {
      // Clear the pending listing ID on success
      setPendingListingId(null);

      // Debug logging
      if (process.env.NODE_ENV === "development") {
        console.log("SavedListingDao onSuccess:", {
          data,
          variables,
          cacheKey,
        });
      }

      // Extract listingId from the successful operation
      let listingId: string | null = null;
      if (variables.where?.listingId_personId?.listingId) {
        listingId = variables.where.listingId_personId.listingId;
      } else if (data?.listingId) {
        listingId = data.listingId;
      }

      if (listingId) {
        // Update the cache with the actual data from the server
        queryClient.setQueryData(
          ["zenstack", "SavedListing"],
          (old: SavedListingInfo[] | undefined) => {
            if (!old) return old;

            const existingIndex = old.findIndex(
              (sl: SavedListingInfo) => sl.listingId === listingId,
            );
            if (existingIndex >= 0) {
              // Update existing record with server data
              const updated = [...old];
              updated[existingIndex] = {
                ...updated[existingIndex],
                ...data,
              } as SavedListingInfo;
              return updated;
            } else {
              // Add new record from server
              return [...old, data as SavedListingInfo];
            }
          },
        );
      }
    },
    onSettled: () => {
      // Avoid refetching the SavedListing list per toggle; our optimistic + onSuccess updates keep it in sync
      // Instead, only invalidate listings that are filtered by favorites so those views can refresh if active
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey;
          return (
            Array.isArray(queryKey) &&
            queryKey[0] === "Listing" &&
            queryKey[1] === "findMany" &&
            queryKey.some((key) => queryKeyHasFavoritesFilter(key))
          );
        },
      });
    },
  });

  /**
   * Check if a listing is saved
   */
  const isListingSaved = (listingId: string): boolean => {
    return savedListingIds.has(listingId);
  };

  /**
   * Toggle the saved status of a listing
   */
  const toggleSavedListing = async (listingId: string): Promise<void> => {
    if (!user?.id || !cacheKey) return;

    try {
      // First, check if we have a record for this listing
      const existingSavedListing = savedListings.find(
        (sl) => sl.listingId === listingId,
      );

      if (existingSavedListing) {
        // Record exists locally: Toggle the isSaved status using composite unique key to avoid temp-id issues
        const newSavedStatus = !existingSavedListing.isSaved;
        await upsertSavedListing.mutateAsync({
          where: {
            listingId_personId: {
              listingId,
              personId: user.id,
            },
          },
          create: {
            listingId,
            personId: user.id,
            isSaved: newSavedStatus,
            createdById: user.id,
          },
          update: {
            isSaved: newSavedStatus,
            updatedById: user.id,
          },
        });
      } else {
        // No record exists: Create or update using composite unique key
        await upsertSavedListing.mutateAsync({
          where: {
            listingId_personId: {
              listingId,
              personId: user.id,
            },
          },
          create: {
            listingId,
            personId: user.id,
            isSaved: true,
            createdById: user.id,
          },
          update: {
            isSaved: true,
            updatedById: user.id,
          },
        });
      }
    } catch (error) {
      console.error("Error toggling save status:", error);
      throw error;
    }
  };

  return {
    savedListings,
    savedListingIds,
    isLoading,
    error,
    isListingSaved,
    toggleSavedListing,
    refetch,
    isPending: upsertSavedListing.isPending,
    // Check if a specific listing is pending
    isListingPending: (listingId: string) => {
      return pendingListingId === listingId;
    },
  };
}
